import React from "react";

/* ================================================================
   Micro Biology — 8 animated templates
   Cell division, virus, bacteria, immune, neuron, DNA, protein, blood
   Detailed microscopic illustration style
   ================================================================ */

// ── 1. Cell Division (Mitosis) ───────────────────────────────────────
export const CellDivision: React.FC<Record<string, unknown>> = (props) => {
  const flowProgress = (props.flowProgress as number) ?? 0;
  const split = flowProgress;
  const offset = split * 80;
  const squeeze = 1 - split * 0.3;
  return (
    <svg viewBox="0 0 600 400" style={{ width: "100%", opacity: (props.opacity as number) ?? 1 }}>
      <text x="300" y="30" textAnchor="middle" fill="#e0e7ff" fontSize="14" fontWeight="bold">Cell Division — Mitosis</text>
      {/* Left daughter cell */}
      <ellipse cx={250 - offset} cy="200" rx={80 * squeeze} ry={70 / squeeze} fill="#bfdbfe" stroke="#3b82f6" strokeWidth="2" opacity={0.5 + split * 0.5} />
      {/* Nucleus left */}
      <circle cx={250 - offset} cy="200" r={20 * squeeze} fill="#1e3a8a" opacity={0.4 + split * 0.6} />
      {/* Chromosomes left */}
      <text x={250 - offset} y="205" textAnchor="middle" fill="#93c5fd" fontSize="12">XX</text>
      {/* Right daughter cell */}
      <ellipse cx={350 + offset} cy="200" rx={80 * squeeze} ry={70 / squeeze} fill="#bfdbfe" stroke="#3b82f6" strokeWidth="2" opacity={0.5 + split * 0.5} />
      <circle cx={350 + offset} cy="200" r={20 * squeeze} fill="#1e3a8a" opacity={0.4 + split * 0.6} />
      <text x={350 + offset} y="205" textAnchor="middle" fill="#93c5fd" fontSize="12">XX</text>
      {/* Cleavage furrow */}
      {split > 0.2 && split < 0.9 && (
        <line x1="300" y1={200 - 70 / squeeze} x2="300" y2={200 + 70 / squeeze} stroke="#6366f1" strokeWidth="2" strokeDasharray="4 3" opacity={1 - split} />
      )}
      {/* Mitotic spindle fibers */}
      {split < 0.7 && (
        <g opacity={0.3}>
          <line x1={250 - offset} y1="200" x2="300" y2="160" stroke="#94a3b8" strokeWidth="1" />
          <line x1={250 - offset} y1="200" x2="300" y2="240" stroke="#94a3b8" strokeWidth="1" />
          <line x1={350 + offset} y1="200" x2="300" y2="160" stroke="#94a3b8" strokeWidth="1" />
          <line x1={350 + offset} y1="200" x2="300" y2="240" stroke="#94a3b8" strokeWidth="1" />
        </g>
      )}
      {/* Labels */}
      <text x="300" y="340" textAnchor="middle" fill="#94a3b8" fontSize="11">
        {split < 0.3 ? "Prophase" : split < 0.5 ? "Metaphase" : split < 0.7 ? "Anaphase" : "Telophase"}
      </text>
      <text x="300" y="370" textAnchor="middle" fill="#6366f1" fontSize="11">Progress: {Math.round(split * 100)}%</text>
    </svg>
  );
};

// ── 2. Virus Structure ───────────────────────────────────────────────
export const VirusStructure: React.FC<Record<string, unknown>> = (props) => {
  const activeIndex = (props.activeIndex as number) ?? 0;
  const parts = ["Capsid", "Envelope", "Spike Protein", "RNA Core", "Lipid Layer"];
  return (
    <svg viewBox="0 0 600 450" style={{ width: "100%", opacity: (props.opacity as number) ?? 1 }}>
      {/* Envelope */}
      <circle cx="300" cy="200" r="100" fill="#f87171" opacity={activeIndex === 1 ? 0.7 : 0.2} stroke={activeIndex === 1 ? "#fff" : "#ef4444"} strokeWidth={activeIndex === 1 ? 2 : 1}>
        {activeIndex === 1 && <animate attributeName="r" values="98;103;98" dur="1.5s" repeatCount="indefinite" />}
      </circle>
      {/* Capsid */}
      <circle cx="300" cy="200" r="70" fill="#fca5a5" opacity={activeIndex === 0 ? 0.8 : 0.3} stroke={activeIndex === 0 ? "#fff" : "#f87171"} strokeWidth={activeIndex === 0 ? 2 : 1} />
      {/* RNA core */}
      <path d="M280,180 Q300,160 320,180 Q300,200 280,180 M290,200 Q310,220 320,200" fill="none" stroke={activeIndex === 3 ? "#fff" : "#1e293b"} strokeWidth={activeIndex === 3 ? 3 : 2} />
      {/* Spike proteins */}
      {Array.from({ length: 12 }).map((_, i) => {
        const a = (i / 12) * Math.PI * 2;
        const sx = 300 + 100 * Math.cos(a);
        const sy = 200 + 100 * Math.sin(a);
        const ex = 300 + 130 * Math.cos(a);
        const ey = 200 + 130 * Math.sin(a);
        return (
          <g key={i}>
            <line x1={sx} y1={sy} x2={ex} y2={ey} stroke={activeIndex === 2 ? "#22c55e" : "#6b7280"} strokeWidth={activeIndex === 2 ? 2 : 1} />
            <circle cx={ex} cy={ey} r={activeIndex === 2 ? 5 : 3} fill={activeIndex === 2 ? "#22c55e" : "#6b7280"}>
              {activeIndex === 2 && <animate attributeName="r" values="4;6;4" dur="1s" repeatCount="indefinite" />}
            </circle>
          </g>
        );
      })}
      {/* Lipid bilayer dots */}
      {Array.from({ length: 24 }).map((_, i) => {
        const a = (i / 24) * Math.PI * 2;
        return (
          <circle key={`lip-${i}`} cx={300 + 95 * Math.cos(a)} cy={200 + 95 * Math.sin(a)} r="2" fill={activeIndex === 4 ? "#fbbf24" : "#94a3b8"} opacity={activeIndex === 4 ? 0.8 : 0.3} />
        );
      })}
      {/* Labels */}
      <text x="300" y="370" textAnchor="middle" fill="#e0e7ff" fontSize="14" fontWeight="bold">Virus Structure</text>
      <text x="300" y="395" textAnchor="middle" fill="#f87171" fontSize="12">{parts[activeIndex]}</text>
    </svg>
  );
};

// ── 3. Bacteria Colony ───────────────────────────────────────────────
export const BacteriaColony: React.FC<Record<string, unknown>> = (props) => {
  const phase = (props.phase as number) ?? 0;
  const bacteria = Array.from({ length: 12 }).map((_, i) => ({
    cx: 150 + (i % 4) * 130 + Math.sin(phase * 0.3 + i) * 8,
    cy: 120 + Math.floor(i / 4) * 100 + Math.cos(phase * 0.4 + i) * 6,
    rx: 20 + (i % 3) * 5,
    ry: 10 + (i % 2) * 4,
    rot: (i * 30 + phase * 2) % 360,
    color: ["#22c55e", "#16a34a", "#15803d", "#86efac"][i % 4],
  }));
  return (
    <svg viewBox="0 0 700 400" style={{ width: "100%", opacity: (props.opacity as number) ?? 1 }}>
      {/* Petri dish */}
      <ellipse cx="350" cy="200" rx="280" ry="170" fill="#fef3c7" stroke="#d97706" strokeWidth="2" />
      <ellipse cx="350" cy="200" rx="270" ry="160" fill="#fffbeb" />
      {/* Bacteria */}
      {bacteria.map((b, i) => (
        <g key={i} transform={`rotate(${b.rot},${b.cx},${b.cy})`}>
          <ellipse cx={b.cx} cy={b.cy} rx={b.rx} ry={b.ry} fill={b.color} opacity="0.8" />
          {/* Flagella */}
          <path d={`M${b.cx + b.rx},${b.cy} Q${b.cx + b.rx + 15},${b.cy - 8} ${b.cx + b.rx + 25},${b.cy}`} fill="none" stroke={b.color} strokeWidth="1">
            <animate attributeName="d" values={`M${b.cx + b.rx},${b.cy} Q${b.cx + b.rx + 15},${b.cy - 8} ${b.cx + b.rx + 25},${b.cy};M${b.cx + b.rx},${b.cy} Q${b.cx + b.rx + 15},${b.cy + 8} ${b.cx + b.rx + 25},${b.cy}`} dur="0.5s" repeatCount="indefinite" />
          </path>
        </g>
      ))}
      <text x="350" y="385" textAnchor="middle" fill="#e0e7ff" fontSize="14" fontWeight="bold">Bacteria Colony</text>
    </svg>
  );
};

// ── 4. Immune Response ───────────────────────────────────────────────
export const ImmuneResponse: React.FC<Record<string, unknown>> = (props) => {
  const activeIndex = (props.activeIndex as number) ?? 0;
  const stages = ["Pathogen Enters", "Detection", "T-Cell Attack", "Antibody Release", "Cleanup"];
  return (
    <svg viewBox="0 0 700 400" style={{ width: "100%", opacity: (props.opacity as number) ?? 1 }}>
      {/* Body border */}
      <rect x="50" y="50" width="600" height="280" rx="12" fill="#fce7f3" opacity="0.3" stroke="#ec4899" strokeWidth="1" />
      {/* Pathogen */}
      <g opacity={activeIndex >= 0 ? 1 : 0.3}>
        <circle cx="150" cy="180" r="20" fill="#22c55e" stroke="#16a34a" strokeWidth="2" />
        {Array.from({ length: 8 }).map((_, i) => {
          const a = (i / 8) * Math.PI * 2;
          return <circle key={i} cx={150 + 25 * Math.cos(a)} cy={180 + 25 * Math.sin(a)} r="3" fill="#16a34a" />;
        })}
        <text x="150" y="220" textAnchor="middle" fill="#22c55e" fontSize="9">Pathogen</text>
      </g>
      {/* Macrophage detection */}
      {activeIndex >= 1 && (
        <g>
          <circle cx="280" cy="160" r="30" fill="#6366f1" opacity="0.6">
            <animate attributeName="r" values="28;33;28" dur="1.5s" repeatCount="indefinite" />
          </circle>
          <text x="280" y="165" textAnchor="middle" fill="#fff" fontSize="9">Macro</text>
          <line x1="180" y1="180" x2="250" y2="168" stroke="#ef4444" strokeWidth="1.5" strokeDasharray="3 2" />
          <text x="280" y="210" textAnchor="middle" fill="#6366f1" fontSize="9">Detected!</text>
        </g>
      )}
      {/* T-cells */}
      {activeIndex >= 2 && (
        <g>
          {[0, 1, 2].map((i) => (
            <circle key={i} cx={380 + i * 25} cy={140 + i * 20} r="12" fill="#ef4444" opacity="0.7">
              <animate attributeName="cx" values={`${400 + i * 25};${370 + i * 25};${400 + i * 25}`} dur="2s" repeatCount="indefinite" />
            </circle>
          ))}
          <text x="420" y="220" textAnchor="middle" fill="#ef4444" fontSize="9">T-Cells Attack</text>
        </g>
      )}
      {/* Antibodies */}
      {activeIndex >= 3 && (
        <g>
          {[0, 1, 2, 3].map((i) => (
            <text key={i} x={480 + (i % 2) * 30} y={140 + Math.floor(i / 2) * 30} fill="#fbbf24" fontSize="16">Y</text>
          ))}
          <text x="510" y="220" textAnchor="middle" fill="#fbbf24" fontSize="9">Antibodies</text>
        </g>
      )}
      {/* Cleanup */}
      {activeIndex >= 4 && (
        <text x="600" y="190" textAnchor="middle" fill="#22c55e" fontSize="12">✓</text>
      )}
      {/* Stage indicator */}
      <text x="350" y="360" textAnchor="middle" fill="#e0e7ff" fontSize="14" fontWeight="bold">{stages[activeIndex]}</text>
    </svg>
  );
};

// ── 5. Neuron Firing ─────────────────────────────────────────────────
export const NeuronFiring: React.FC<Record<string, unknown>> = (props) => {
  const phase = (props.phase as number) ?? 0;
  const signalPos = (phase * 0.2) % 1;
  return (
    <svg viewBox="0 0 800 350" style={{ width: "100%", opacity: (props.opacity as number) ?? 1 }}>
      {/* Dendrites (left side) */}
      {[-40, -20, 0, 20, 40].map((dy, i) => (
        <line key={i} x1="50" y1={175 + dy * 2} x2="150" y2={175 + dy} stroke="#818cf8" strokeWidth="2" />
      ))}
      {/* Cell body (soma) */}
      <circle cx="180" cy="175" r="30" fill="#6366f1" stroke="#4f46e5" strokeWidth="2" />
      <circle cx="180" cy="175" r="12" fill="#4f46e5" />
      <text x="180" y="180" textAnchor="middle" fill="#c7d2fe" fontSize="8">Soma</text>
      {/* Axon */}
      <line x1="210" y1="175" x2="600" y2="175" stroke="#818cf8" strokeWidth="4" />
      {/* Myelin sheaths */}
      {[0, 1, 2, 3].map((i) => (
        <rect key={i} x={240 + i * 90} y="160" width="60" height="30" rx="10" fill="#c7d2fe" opacity="0.3" stroke="#6366f1" strokeWidth="1" />
      ))}
      {/* Signal pulse traveling along axon */}
      <circle cx={210 + signalPos * 390} cy="175" r="8" fill="#fbbf24" opacity="0.8">
        <animate attributeName="r" values="6;10;6" dur="0.5s" repeatCount="indefinite" />
      </circle>
      {/* Axon terminal / synaptic buttons */}
      {[-15, 0, 15].map((dy, i) => (
        <g key={i}>
          <line x1="600" y1="175" x2="650" y2={170 + dy} stroke="#818cf8" strokeWidth="2" />
          <circle cx="660" cy={170 + dy} r="8" fill="#6366f1" />
        </g>
      ))}
      {/* Neurotransmitters released */}
      {signalPos > 0.8 && (
        <g>
          {[0, 1, 2, 3].map((i) => (
            <circle key={i} cx={670 + i * 8} cy={165 + (i % 2) * 15} r="3" fill="#fbbf24" opacity="0.6">
              <animate attributeName="cx" values={`${668 + i * 8};${680 + i * 8};${668 + i * 8}`} dur="1s" repeatCount="indefinite" />
            </circle>
          ))}
        </g>
      )}
      {/* Labels */}
      <text x="80" y="260" fill="#818cf8" fontSize="10">Dendrites</text>
      <text x="400" y="220" textAnchor="middle" fill="#818cf8" fontSize="10">Axon (myelinated)</text>
      <text x="660" y="220" textAnchor="middle" fill="#818cf8" fontSize="10">Synapse</text>
      <text x="400" y="310" textAnchor="middle" fill="#e0e7ff" fontSize="14" fontWeight="bold">Neuron — Action Potential</text>
    </svg>
  );
};

// ── 6. DNA Replication ───────────────────────────────────────────────
export const DNAReplication: React.FC<Record<string, unknown>> = (props) => {
  const phase = (props.phase as number) ?? 0;
  const unzipPoint = 300 + Math.sin(phase * 0.2) * 50;
  return (
    <svg viewBox="0 0 700 350" style={{ width: "100%", opacity: (props.opacity as number) ?? 1 }}>
      {/* Double helix - left intact strand */}
      {Array.from({ length: 20 }).map((_, i) => {
        const x = 80 + i * 15;
        if (x > unzipPoint) return null;
        const y1 = 150 + Math.sin(i * 0.7 + phase * 0.3) * 30;
        const y2 = 150 - Math.sin(i * 0.7 + phase * 0.3) * 30;
        return (
          <g key={`l-${i}`}>
            <circle cx={x} cy={y1} r="4" fill="#3b82f6" />
            <circle cx={x} cy={y2} r="4" fill="#ef4444" />
            <line x1={x} y1={y1} x2={x} y2={y2} stroke="#6b7280" strokeWidth="1" opacity="0.4" />
          </g>
        );
      })}
      {/* Replication fork */}
      <line x1={unzipPoint} y1="110" x2={unzipPoint} y2="190" stroke="#fbbf24" strokeWidth="2" strokeDasharray="3 2" />
      <text x={unzipPoint} y="100" textAnchor="middle" fill="#fbbf24" fontSize="9">Fork</text>
      {/* Right unzipped strands */}
      {Array.from({ length: 15 }).map((_, i) => {
        const x = unzipPoint + 15 + i * 15;
        if (x > 650) return null;
        const spread = Math.min(i * 3, 40);
        const y1 = 150 + spread + Math.sin(i * 0.7) * 15;
        const y2 = 150 - spread - Math.sin(i * 0.7) * 15;
        return (
          <g key={`r-${i}`}>
            <circle cx={x} cy={y1} r="4" fill="#3b82f6" opacity="0.7" />
            {/* New complementary strand */}
            <circle cx={x} cy={y1 - 10} r="3" fill="#93c5fd" opacity="0.5" />
            <circle cx={x} cy={y2} r="4" fill="#ef4444" opacity="0.7" />
            <circle cx={x} cy={y2 + 10} r="3" fill="#fca5a5" opacity="0.5" />
          </g>
        );
      })}
      {/* Helicase enzyme */}
      <circle cx={unzipPoint} cy="150" r="10" fill="#fbbf24" stroke="#f59e0b" strokeWidth="2">
        <animate attributeName="r" values="9;12;9" dur="1s" repeatCount="indefinite" />
      </circle>
      <text x={unzipPoint} y="154" textAnchor="middle" fill="#1e293b" fontSize="7">H</text>
      {/* Legend */}
      <circle cx="80" cy="280" r="5" fill="#3b82f6" /><text x="95" y="284" fill="#94a3b8" fontSize="9">Template strand</text>
      <circle cx="80" cy="300" r="5" fill="#ef4444" /><text x="95" y="304" fill="#94a3b8" fontSize="9">Complementary strand</text>
      <circle cx="250" cy="280" r="5" fill="#93c5fd" /><text x="265" y="284" fill="#94a3b8" fontSize="9">New strand</text>
      <text x="350" y="340" textAnchor="middle" fill="#e0e7ff" fontSize="14" fontWeight="bold">DNA Replication</text>
    </svg>
  );
};

// ── 7. Protein Folding ───────────────────────────────────────────────
export const ProteinFolding: React.FC<Record<string, unknown>> = (props) => {
  const flowProgress = (props.flowProgress as number) ?? 0;
  // Chain folds from linear to compact
  const fold = flowProgress;
  const numBeads = 15;
  return (
    <svg viewBox="0 0 600 400" style={{ width: "100%", opacity: (props.opacity as number) ?? 1 }}>
      <text x="300" y="30" textAnchor="middle" fill="#e0e7ff" fontSize="14" fontWeight="bold">Protein Folding</text>
      {/* Amino acid chain */}
      {Array.from({ length: numBeads }).map((_, i) => {
        // Linear position
        const lx = 80 + i * 30;
        const ly = 200;
        // Folded position (spiral)
        const angle = (i / numBeads) * Math.PI * 3;
        const foldR = 40 + (i / numBeads) * 50;
        const fx = 300 + foldR * Math.cos(angle);
        const fy = 200 + foldR * Math.sin(angle) * 0.7;
        // Interpolate
        const x = lx + (fx - lx) * fold;
        const y = ly + (fy - ly) * fold;
        const colors = ["#ef4444", "#3b82f6", "#22c55e", "#f59e0b", "#8b5cf6"];
        return (
          <g key={i}>
            {i > 0 && (() => {
              const plx = 80 + (i - 1) * 30;
              const pfx = 300 + (40 + ((i - 1) / numBeads) * 50) * Math.cos(((i - 1) / numBeads) * Math.PI * 3);
              const pfy = 200 + (40 + ((i - 1) / numBeads) * 50) * Math.sin(((i - 1) / numBeads) * Math.PI * 3) * 0.7;
              const px = plx + (pfx - plx) * fold;
              const py = 200 + (pfy - 200) * fold;
              return <line x1={px} y1={py} x2={x} y2={y} stroke="#6b7280" strokeWidth="2" />;
            })()}
            <circle cx={x} cy={y} r="8" fill={colors[i % colors.length]} stroke="#1e293b" strokeWidth="1" />
          </g>
        );
      })}
      {/* Stage label */}
      <text x="300" y="350" textAnchor="middle" fill="#94a3b8" fontSize="12">
        {fold < 0.3 ? "Primary Structure (chain)" : fold < 0.7 ? "Folding..." : "Tertiary Structure (3D)"}
      </text>
      <text x="300" y="375" textAnchor="middle" fill="#6366f1" fontSize="11">Folding: {Math.round(fold * 100)}%</text>
    </svg>
  );
};

// ── 8. Blood Cells ───────────────────────────────────────────────────
export const BloodCells: React.FC<Record<string, unknown>> = (props) => {
  const phase = (props.phase as number) ?? 0;
  const activeIndex = (props.activeIndex as number) ?? 0;
  const cellTypes = ["Red Blood Cell", "White Blood Cell", "Platelet"];
  return (
    <svg viewBox="0 0 700 400" style={{ width: "100%", opacity: (props.opacity as number) ?? 1 }}>
      {/* Blood vessel */}
      <rect x="0" y="100" width="700" height="200" fill="#fca5a5" opacity="0.15" rx="50" />
      <ellipse cx="0" cy="200" rx="30" ry="100" fill="#fca5a5" opacity="0.2" />
      <ellipse cx="700" cy="200" rx="30" ry="100" fill="#fca5a5" opacity="0.2" />
      {/* Flowing red blood cells */}
      {Array.from({ length: 10 }).map((_, i) => {
        const x = (100 + i * 65 + phase * 10) % 700;
        const y = 160 + Math.sin(i * 1.3 + phase * 0.2) * 40;
        return (
          <ellipse key={`rbc-${i}`} cx={x} cy={y} rx="18" ry="10" fill={activeIndex === 0 ? "#ef4444" : "#fca5a5"} opacity={activeIndex === 0 ? 0.8 : 0.4}
                   transform={`rotate(${i * 20},${x},${y})`} />
        );
      })}
      {/* White blood cells (larger, fewer) */}
      {[200, 450].map((bx, i) => {
        const x = bx + Math.sin(phase * 0.15 + i) * 20;
        const y = 200 + Math.cos(phase * 0.2 + i) * 15;
        return (
          <circle key={`wbc-${i}`} cx={x} cy={y} r={activeIndex === 1 ? 22 : 18} fill={activeIndex === 1 ? "#e0e7ff" : "#cbd5e1"} opacity={activeIndex === 1 ? 0.8 : 0.3}
                  stroke={activeIndex === 1 ? "#6366f1" : "none"} strokeWidth="2">
            {activeIndex === 1 && <animate attributeName="r" values="18;24;18" dur="2s" repeatCount="indefinite" />}
          </circle>
        );
      })}
      {/* Platelets (tiny) */}
      {Array.from({ length: 6 }).map((_, i) => {
        const x = 120 + i * 90 + Math.sin(phase * 0.3 + i) * 10;
        const y = 220 + Math.cos(phase * 0.4 + i) * 20;
        return (
          <ellipse key={`plt-${i}`} cx={x} cy={y} rx="6" ry="4" fill={activeIndex === 2 ? "#fbbf24" : "#d4a574"} opacity={activeIndex === 2 ? 0.9 : 0.3}
                   transform={`rotate(${i * 40 + phase * 3},${x},${y})`} />
        );
      })}
      <text x="350" y="360" textAnchor="middle" fill="#e0e7ff" fontSize="14" fontWeight="bold">{cellTypes[activeIndex]}</text>
    </svg>
  );
};
